import { useState, useEffect } from 'react';
import { getProducts, getCategories, uploadProductImage } from '../../../services/productsService';
import { apiPost, apiPut, apiDelete } from '../../../services/api';
import ConfirmModal from '../components/ConfirmModal';

const styles = {
  container: { padding: '24px', maxWidth: '1200px' },
  header: { marginBottom: '20px' },
  title: { margin: 0, fontSize: '28px', fontWeight: 700 },
  subtitle: { color: '#666', marginTop: '6px' },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '18px',
    marginTop: '16px'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px', fontSize: '13px', color: '#555', borderBottom: '1px solid #e0e0e0' },
  td: { padding: '12px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
  thumb: { width: 48, height: 48, objectFit: 'cover', borderRadius: '8px' },
  btnPrimary: { padding: '8px 14px', background: '#1a4d8c', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  btnGhost: { padding: '8px 14px', background: '#f4f6f8', color: '#111', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  btnDanger: { padding: '8px 14px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, marginLeft: '8px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', color: '#555', fontWeight: 600 },
  input: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' },
  textarea: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', minHeight: '80px', resize: 'vertical' },
  row: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '18px' },
  empty: { textAlign: 'center', padding: '24px', color: '#666' },
  badge: { 
    display: 'inline-block', 
    padding: '4px 8px', 
    borderRadius: '4px', 
    fontSize: '11px', 
    fontWeight: 600,
    marginLeft: '8px'
  },
  badgeSuccess: { background: '#e8f5e9', color: '#2e7d32' },
  badgeWarning: { background: '#fff3e0', color: '#f57c00' }
};

const ProductosView = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); 
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    precio: '',
    categoria_id: '',
    descripcion: '',
    imagen_principal_url: '',
    stock: '0',
    destacado: false,
    activo: true
  });
  const [imageFile, setImageFile] = useState(null);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const { productos } = await getProducts({ limit: 100 });
      setProducts(productos);
    } catch (error) {
      showMessage('Error al cargar productos: ' + error.message, true);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
      if (cats.length > 0 && !form.categoria_id) {
        setField('categoria_id', cats[0].id);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const showMessage = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 4000);
  };

  const openNew = () => {
    setEditingId('new');
    setForm({
      nombre: '',
      precio: '',
      categoria_id: categories.length > 0 ? categories[0].id : '',
      descripcion: '',
      imagen_principal_url: '',
      stock: '0',
      destacado: false,
      activo: true
    });
    setImageFile(null);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      nombre: p.nombre,
      precio: String(p.precio),
      categoria_id: p.categoria_id || (categories.length > 0 ? categories[0].id : ''),
      descripcion: p.descripcion || '',
      imagen_principal_url: p.imagen_principal_url || '',
      stock: String(p.stock || 0),
      destacado: p.destacado || false,
      activo: p.activo !== false
    });
    setImageFile(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      nombre: '',
      precio: '',
      categoria_id: categories.length > 0 ? categories[0].id : '',
      descripcion: '',
      imagen_principal_url: '',
      stock: '0',
      destacado: false,
      activo: true
    });
    setImageFile(null);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      showMessage('El nombre es obligatorio', true);
      return;
    }
    const precio = parseFloat(form.precio);
    if (isNaN(precio) || precio < 0) {
      showMessage('Precio debe ser un número válido', true);
      return;
    }
    const stock = parseInt(form.stock, 10);
    if (isNaN(stock) || stock < 0) {
      showMessage('Stock debe ser un número válido', true);
      return;
    }
    if (!form.categoria_id) {
      showMessage('Debes seleccionar una categoría', true);
      return;
    }

    setSaving(true);
    try {
      let imagenUrl = form.imagen_principal_url;
      
      if (imageFile) {
        setUploading(true);
        try {
          imagenUrl = await uploadProductImage(imageFile, editingId !== 'new' ? editingId : null);
          showMessage('Imagen subida correctamente');
        } catch (uploadError) {
          showMessage('Advertencia: No se pudo subir la imagen. Se guardará sin imagen.', true);
          imagenUrl = null;
        } finally {
          setUploading(false);
        }
      }

      const productData = {
        nombre: form.nombre.trim(),
        precio: precio,
        categoria_id: parseInt(form.categoria_id),
        descripcion: (form.descripcion || '').trim(),
        imagen_principal_url: imagenUrl,
        stock: stock,
        destacado: form.destacado,
        activo: form.activo
      };

      let response;
      if (editingId === 'new') {
        response = await apiPost('/api/ecommerce/productos', productData);
      } else {
        response = await apiPut(`/api/ecommerce/productos/${editingId}`, productData);
      }

      const data = await response.json();
      
      if (data.success) {
        showMessage(editingId === 'new' ? 'Producto creado exitosamente' : 'Producto actualizado exitosamente');
        await loadProducts();
        cancelEdit();
      } else {
        throw new Error(data.error || 'Error al guardar producto');
      }
    } catch (error) {
      showMessage(error.message || 'Error al guardar producto', true);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { id, nombre } = confirmDelete;
    setDeleting(true);
    try {
      const response = await apiDelete(`/api/ecommerce/productos/${id}`);
      const data = await response.json();
      if (data.success) {
        setConfirmDelete(null);
        showMessage('Producto eliminado exitosamente');
        await loadProducts();
        if (editingId === id) cancelEdit();
      } else {
        throw new Error(data.error || 'Error al eliminar producto');
      }
    } catch (error) {
      showMessage(error.message || 'Error al eliminar producto', true);
    } finally {
      setDeleting(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
    if (img.startsWith('http')) return img;
    return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}${img}` : img;
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.nombre : 'Sin categoría';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Tienda / Productos</h1>
          <p style={styles.subtitle}>Cargando productos...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Tienda / Productos</h1>
        <p style={styles.subtitle}>
          Gestiona los productos de la tienda. Total: {products.length} producto{products.length !== 1 ? 's' : ''}
        </p>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '8px',
          background: message.isError ? '#ffebee' : '#e8f5e9',
          color: message.isError ? '#c62828' : '#2e7d32',
          fontWeight: 600
        }}>
          {message.text}
        </div>
      )}

      {editingId !== null ? (
        <div style={styles.card}>
          <h2 style={{ marginTop: 0 }}>{editingId === 'new' ? '🆕 Nuevo producto' : '✏️ Editar producto'}</h2>
          <div style={styles.grid}>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Nombre del producto *</label>
              <input 
                style={styles.input} 
                value={form.nombre} 
                onChange={e => setField('nombre', e.target.value)} 
                placeholder="Ej: Cinturón SBD 13mm"
                maxLength={200}
              />
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>Precio (CLP) *</label>
              <input 
                type="number" 
                style={styles.input} 
                value={form.precio} 
                onChange={e => setField('precio', e.target.value)} 
                placeholder="189990"
                min="0"
                step="1"
              />
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>Stock *</label>
              <input 
                type="number" 
                style={styles.input} 
                value={form.stock} 
                onChange={e => setField('stock', e.target.value)} 
                placeholder="10"
                min="0"
                step="1"
              />
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>Categoría *</label>
              <select 
                style={styles.input} 
                value={form.categoria_id} 
                onChange={e => setField('categoria_id', e.target.value)}
              >
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>
                <input 
                  type="checkbox" 
                  checked={form.destacado} 
                  onChange={e => setField('destacado', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Producto destacado ⭐
              </label>
            </div>
            
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Descripción</label>
              <textarea 
                style={styles.textarea} 
                value={form.descripcion} 
                onChange={e => setField('descripcion', e.target.value)} 
                placeholder="Descripción detallada del producto..."
                maxLength={1000}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                {form.descripcion.length}/1000 caracteres
              </small>
            </div>
            
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Imagen del producto</label>
              {form.imagen_principal_url && (
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <img 
                    src={getImageUrl(form.imagen_principal_url)} 
                    alt="Preview" 
                    style={{ ...styles.thumb, width: 120, height: 120 }}
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
                    }}
                  />
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Imagen actual</p>
                    <p style={{ margin: '4px 0 0 0', color: '#999', fontSize: '12px' }}>
                      Sube una nueva imagen para reemplazarla
                    </p>
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={e => setImageFile(e.target.files[0] || null)}
                style={{ fontSize: '14px' }}
              />
              {imageFile && (
                <p style={{ margin: '8px 0 0 0', color: '#1a4d8c', fontSize: '13px' }}>
                  📎 Archivo seleccionado: {imageFile.name}
                </p>
              )}
              {uploading && (
                <p style={{ margin: '8px 0 0 0', color: '#f57c00', fontSize: '13px' }}>
                  ⏳ Subiendo imagen a S3...
                </p>
              )}
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                Formatos: JPG, PNG, WEBP. Tamaño recomendado: 800x800px
              </small>
            </div>
          </div>
          
          <div style={styles.row}>
            <button 
              style={styles.btnPrimary} 
              onClick={handleSave} 
              disabled={saving || uploading}
            >
              {saving ? '⏳ Guardando…' : uploading ? '📤 Subiendo imagen...' : '💾 Guardar producto'}
            </button>
            <button 
              style={styles.btnGhost} 
              onClick={cancelEdit} 
              disabled={saving || uploading}
            >
              ❌ Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ ...styles.row, justifyContent: 'flex-end' }}>
            <button style={styles.btnPrimary} onClick={openNew}>
              ➕ Agregar producto
            </button>
          </div>
          
          <div style={styles.card}>
            {products.length === 0 ? (
              <div style={styles.empty}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>📦 No hay productos</p>
                <p style={{ color: '#999' }}>Agrega tu primer producto para comenzar</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Imagen</th>
                      <th style={styles.th}>Producto</th>
                      <th style={styles.th}>Precio</th>
                      <th style={styles.th}>Stock</th>
                      <th style={styles.th}>Categoría</th>
                      <th style={styles.th}>Estado</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id}>
                        <td style={styles.td}>
                          <img 
                            src={getImageUrl(p.imagen_principal_url)} 
                            alt={p.nombre} 
                            style={styles.thumb}
                            onError={(e) => {
                              e.target.src = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
                            }}
                          />
                        </td>
                        <td style={styles.td}>
                          <strong>{p.nombre}</strong>
                          {p.destacado && <span style={{ ...styles.badge, ...styles.badgeWarning }}>⭐ Destacado</span>}
                        </td>
                        <td style={styles.td}>${(p.precio || 0).toLocaleString('es-CL')}</td>
                        <td style={styles.td}>
                          {p.stock > 0 ? (
                            <span style={{ color: p.stock < 5 ? '#f57c00' : '#2e7d32' }}>
                              {p.stock} unidades
                            </span>
                          ) : (
                            <span style={{ color: '#c62828' }}>Sin stock</span>
                          )}
                        </td>
                        <td style={styles.td}>{getCategoryName(p.categoria_id)}</td>
                        <td style={styles.td}>
                          {p.activo ? (
                            <span style={{ ...styles.badge, ...styles.badgeSuccess }}>✓ Activo</span>
                          ) : (
                            <span style={{ ...styles.badge, background: '#ffebee', color: '#c62828' }}>✗ Inactivo</span>
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button 
                            style={styles.btnGhost} 
                            onClick={() => openEdit(p)}
                          >
                            ✏️ Editar
                          </button>
                          <button 
                            style={styles.btnDanger} 
                            onClick={() => setConfirmDelete({ id: p.id, nombre: p.nombre })}
                          >
                            🗑️ Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar producto"
        message={confirmDelete ? `¿Estás seguro de eliminar "${confirmDelete.nombre}"?` : ''}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default ProductosView;